/**
 * 403 `module.inactive` — module fermé pour le locataire (UPT-2069 L2).
 *
 * Le serveur refuse toute l'API d'un module qui n'est ni actif ni en essai pour le locataire (barrière
 * `ModuleGateInterceptor`) avec `{ "code": "module.inactive", "module": "quenotte", "message": "…" }`. C'est un ÉTAT,
 * pas une panne : le client le traduit en `ModuleInactiveError`, que les écrans montrent comme « module non activé »
 * (ModuleInactiveState) au lieu d'un message d'erreur générique ou d'un toast « accès refusé ».
 *
 * Patron : core/api/featureDisabled.ts (403 `feature.disabled`, UPT-2056) — mêmes formes de rejet reconnues (axios nu,
 * `{ error, status }` du shell legacy). Un 403 sans ce code (droit manquant) n'est PAS concerné.
 *
 * Intercepteur HTTP commun : `installModuleInactiveInterceptor(axios)` (posé par ClientProviders) publie chaque refus
 * sur un petit signal (`subscribeModuleInactive`) ; la page d'un module ouverte par l'URL écoute ce signal
 * (ModuleInactiveBoundary) et se remplace par l'état « module non activé ». Le rejet lui-même est transmis tel quel à
 * l'appelant : rien ne change pour les écrans qui gèrent déjà leurs erreurs.
 */
import type { AxiosInstance } from 'axios';
import { forbiddenBodyOf } from './featureDisabled';

export const MODULE_INACTIVE_CODE = 'module.inactive';

/** Module inconnu du corps de la réponse (clé absente) : l'appelant décide d'un repli. */
const UNKNOWN_MODULE = 'unknown';

export class ModuleInactiveError extends Error {
  readonly code = MODULE_INACTIVE_CODE;
  readonly module: string;
  readonly trialExpired: boolean;

  constructor(module: string, trialExpired = false) {
    super(`Module "${module}" is not activated for this tenant`);
    this.name = 'ModuleInactiveError';
    this.module = module;
    this.trialExpired = trialExpired;
  }
}

/**
 * Traduit un rejet de requête en `ModuleInactiveError` si c'est un 403 `module.inactive`, sinon null.
 * `fallbackModule` : module de l'API appelée, utilisé si le corps ne nomme pas le sien.
 */
export function toModuleInactiveError(error: unknown, fallbackModule?: string): ModuleInactiveError | null {
  if (error instanceof ModuleInactiveError) return error;
  const body = forbiddenBodyOf(error);
  if (body === null || body.code !== MODULE_INACTIVE_CODE) return null;
  const named = typeof body.module === 'string' && body.module.trim() !== '' ? body.module.trim() : null;
  return new ModuleInactiveError(named ?? fallbackModule ?? UNKNOWN_MODULE, body.trialExpired === true);
}

/** Vrai si `error` est un 403 `module.inactive` (de `module` si précisé). */
export function isModuleInactiveError(error: unknown, module?: string): boolean {
  const translated = toModuleInactiveError(error, module);
  return translated !== null && (module === undefined || translated.module === module);
}

// ── Signal : le dernier refus reçu par l'application ────────────────────────────────────────────────────────────

export type ModuleInactiveSignal = {
  /** Code du module fermé (canonique, tel que servi par le serveur). */
  module: string;
  /** URL de la requête refusée, si connue (diagnostic). */
  url: string | null;
  /** Numéro d'ordre croissant : deux refus du même module restent deux événements. */
  seq: number;
  trialExpired?: boolean;
};

type Listener = (signal: ModuleInactiveSignal) => void;

const listeners = new Set<Listener>();
let current: ModuleInactiveSignal | null = null;
let sequence = 0;

/** Publie un refus `module.inactive` à tous les abonnés. */
export function reportModuleInactive(module: string, url: string | null = null, trialExpired = false): ModuleInactiveSignal {
  sequence += 1;
  current = { module, url, seq: sequence };
  if (trialExpired) current.trialExpired = true;
  const signal = current;
  listeners.forEach((listener) => {
    try {
      listener(signal);
    } catch {
      // Un abonné défaillant n'empêche pas les autres d'être prévenus.
    }
  });
  return signal;
}

export function getModuleInactiveSignal(): ModuleInactiveSignal | null {
  return current;
}

/** S'abonne aux refus `module.inactive` PUBLIÉS APRÈS l'abonnement ; renvoie la fonction de désabonnement. */
export function subscribeModuleInactive(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Oublie le dernier refus (tests, changement de locataire). */
export function resetModuleInactiveSignal(): void {
  current = null;
}

// ── Intercepteur HTTP commun ─────────────────────────────────────────────────────────────────────────────────────

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

function configOf(error: unknown): Record<string, unknown> | null {
  if (!isObject(error)) return null;
  if (isObject(error.config)) return error.config;
  if (isObject(error.error) && isObject(error.error.config)) return error.error.config;
  return null;
}

function requestUrlOf(error: unknown): string | null {
  const config = configOf(error);
  return config !== null && typeof config.url === 'string' ? config.url : null;
}

/**
 * Traitement d'un rejet par l'intercepteur commun : publie le refus s'il s'agit d'un 403 `module.inactive`, puis
 * renvoie le rejet INCHANGÉ (l'appelant garde la main sur son affichage).
 */
export function handleModuleInactiveRejection(error: unknown): Promise<never> {
  const refusal = toModuleInactiveError(error);
  if (refusal !== null) {
    reportModuleInactive(refusal.module, requestUrlOf(error), refusal.trialExpired);
  }
  // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- le rejet d'axios est transmis tel quel
  return Promise.reject(error);
}

const installed = new WeakSet<object>();

/** Pose l'intercepteur de réponse sur un client axios (une seule fois par client). */
export function installModuleInactiveInterceptor(client: Pick<AxiosInstance, 'interceptors'>): void {
  if (installed.has(client)) return;
  installed.add(client);
  client.interceptors.response.use((response) => response, handleModuleInactiveRejection);
}
